import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { SectionHead, Reveal } from "./effects";

const faqs = [
  {
    q: "How long does a project take?",
    a: "Short-form edits are typically delivered within 48–72 hours. Long-form YouTube videos take 5–10 business days depending on length and complexity. Need it faster? Rush delivery is available on request.",
  },
  {
    q: "How many revisions are included?",
    a: "Every project includes two full revision rounds. Most clients approve within one — I work closely with your feedback until the edit feels exactly right.",
  },
  {
    q: "Do you edit short-form content?",
    a: "Absolutely — it's my specialty. Reels, TikToks and YouTube Shorts with hooks, kinetic captions and retention-first pacing. I also repurpose long-form content into short-form batches.",
  },
  {
    q: "Can you create motion graphics?",
    a: "Yes. Kinetic typography, animated charts, logo animations, lower thirds and full explainer-style sequences — all designed to match your brand's visual language.",
  },
  {
    q: "What are your pricing models?",
    a: "Pricing is available on inquiry and tailored to your scope — per-video, monthly retainers for creators publishing consistently, and project packages for brands. Share your project details and I'll send a custom quote within 24 hours.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState(0);

  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 sm:py-32">
      <div className="max-w-3xl mx-auto px-5 sm:px-8">
        <SectionHead
          testId="faq"
          eyebrow="FAQ"
          align="center"
          title={
            <>
              Questions, <span className="text-gradient-blue">answered</span>
            </>
          }
        />
        <div className="space-y-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={f.q} delay={i * 0.06}>
                <div
                  data-testid={`faq-item-${i}`}
                  className={`rounded-2xl border transition-all duration-500 overflow-hidden ${
                    isOpen
                      ? "border-[#D4AF37]/50 bg-[#D4AF37]/5 shadow-[0_0_40px_rgba(212,175,55,0.12)]"
                      : "border-white/10 bg-[#0a0a0c] hover:border-white/20"
                  }`}
                >
                  <button
                    data-testid={`faq-question-${i}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-display text-base sm:text-lg font-medium">{f.q}</span>
                    <motion.span
                      animate={{ rotate: isOpen ? 45 : 0 }}
                      transition={{ duration: 0.3 }}
                      className={`shrink-0 flex h-8 w-8 items-center justify-center rounded-full border ${
                        isOpen ? "border-[#D4AF37] text-[#F5DE81]" : "border-white/15 text-slate-400"
                      }`}
                    >
                      <Plus size={16} />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <p data-testid={`faq-answer-${i}`} className="px-6 pb-6 text-sm text-slate-400 leading-relaxed">
                          {f.a}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
